<template>
  <div class="overview-page">
    <div class="overview-shell">
      <aside class="overview-sidebar">
        <div class="overview-hero">
      <div class="page-header">
        <div class="page-header__main">
          <div class="page-eyebrow">连山数据总览</div>
          <div class="page-title">千川账户消耗总览</div>
          <div class="page-subtitle">首页指标支持直接跳转；请点击下方数据卡片，按当前筛选范围查看实时的消耗、成交金额或 ROI 明细。</div>
          <div class="page-header__tags">
            <span class="page-tag">实时概览卡</span>
            <span class="page-tag">异步统计主表</span>
            <span class="page-tag page-tag--warn">新授权账户建议先同步</span>
          </div>
        </div>
        <div class="header-actions">
          <el-radio-group v-model="viewMode" size="small" @change="handleViewChange">
            <el-radio-button label="tree">分层汇总</el-radio-button>
            <el-radio-button label="flat">全量平铺</el-radio-button>
          </el-radio-group>
        </div>
      </div>

      <div class="dashboard-card-grid">
        <div
          v-for="card in dashboardCards"
          :key="card.metricType"
          class="dashboard-card"
          :class="{ active: detailMetricType === card.metricType && detailDrawerVisible }"
          @click="openMetricDetail(card.metricType)"
        >
          <div class="dashboard-card__label">{{ card.label }}</div>
          <div class="dashboard-card__value">{{ card.value }}</div>
          <div class="dashboard-card__meta">{{ card.meta }}</div>
        </div>
      </div>
        </div>

      </aside>

      <section class="overview-content">
        <div id="overview-filters" class="filter-card">
      <div class="filter-card__header">
        <div class="filter-card__title">筛选与同步</div>
        <div class="filter-card__subtitle">先确定筛选范围，再点击左侧概览卡片查看连山实时明细，或同步异步统计主表做稳定筛查。</div>
      </div>
      <div class="filter-row">
        <el-select
          v-if="isManager"
          v-model="searchForm.agentId"
          clearable
          filterable
          placeholder="代理"
          class="filter-item"
        >
          <el-option
            v-for="item in agentOptions"
            :key="agentOptionValue(item)"
            :label="agentOptionLabel(item)"
            :value="agentOptionValue(item)"
          />
        </el-select>

        <el-select
          v-model="searchForm.groupId"
          clearable
          filterable
          placeholder="账户分组"
          class="filter-item"
          @change="handleGroupChange"
        >
          <el-option
            v-for="item in advertiserGroupOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>

        <el-select
          v-model="searchForm.advertiserId"
          clearable
          filterable
          placeholder="广告账户"
          class="filter-item"
        >
          <el-option
            v-for="item in filteredAdvertiserOptions"
            :key="item.advertiserId"
            :label="`${item.name} / ${item.advertiserId}`"
            :value="item.advertiserId"
          />
        </el-select>

        <el-input
          v-model.trim="searchForm.storeName"
          clearable
          placeholder="店铺 / 商户名称"
          class="filter-item"
        />

        <el-input
          v-model.trim="searchForm.aweMeId"
          clearable
          placeholder="抖音号ID"
          class="filter-item"
        />

        <el-date-picker
          v-model="searchForm.dateRange"
          type="daterange"
          value-format="yyyy-MM-dd"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          class="filter-item date-range"
        />

        <el-checkbox v-model="onlyWithConsumption" class="filter-check">仅看有消耗数据</el-checkbox>

        <div class="filter-row__actions">
        <el-button
          :loading="statisticsLoading"
          :disabled="statisticsDisabled || statisticsSyncing"
          @click="handleStatistics"
        >
          {{ statisticsSyncing ? "同步中" : "数据同步" }}
        </el-button>

        <el-button type="primary" @click="handleSearch">查询列表</el-button>
        <el-button @click="handleReset">重置</el-button>
        </div>
      </div>

      <div class="cache-tip">
        <strong class="cache-tip__headline">主表格仍沿用异步同步缓存；左侧概览卡（点击可查看明细）和详情抽屉直接查询当前筛选范围的连山实时数据。</strong>
        <strong class="cache-tip__headline cache-tip__headline--warn">数据来源来自连山数据库，实时数据可能存在延迟、遗漏；新授权账户可能存在统计不准情况。</strong>
        <strong class="cache-tip__headline cache-tip__headline--warn">异步同步缓存结果通常更完整、更准确，建议新授权账户（首次授权且未满 24 小时的账户）优先使用“数据同步”后查询主表。</strong>
        <span class="cache-tip__status" :class="statusClass">{{ statusText }}</span>
        <span v-if="cacheTip" class="cache-tip__meta">{{ cacheTip }}</span>
      </div>
    </div>

    <div id="overview-table" class="table-card">
      <div class="table-card__header">
        <div class="table-card__title">异步统计主表</div>
        <div class="table-card__subtitle">更适合全量筛查、稳定对账和新授权账户校验；左侧实时概览（支持点击交互）更适合快速判断趋势。</div>
      </div>
      <el-table
        v-if="viewMode === 'tree'"
        :data="treeData"
        stripe
        row-key="nodeId"
        :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
        v-loading="loading"
        style="width: 100%"
      >
        <el-table-column prop="name" label="名称" min-width="220" />
        <el-table-column prop="nodeType" label="层级" width="120">
          <template slot-scope="{ row }">
            {{ formatNodeType(row.nodeType) }}
          </template>
        </el-table-column>
        <el-table-column prop="advertiserId" label="广告账户ID" min-width="140" />
        <el-table-column prop="aweMeId" label="抖音号ID" min-width="140" />
        <el-table-column label="消耗" min-width="120">
          <template slot-scope="{ row }">{{ formatMoney(row.totalConsumption) }}</template>
        </el-table-column>
        <el-table-column label="成交金额" min-width="120">
          <template slot-scope="{ row }">{{ formatMoney(row.totalAmount) }}</template>
        </el-table-column>
        <el-table-column label="支付金额" min-width="120">
          <template slot-scope="{ row }">{{ formatMoney(row.payAmount) }}</template>
        </el-table-column>
        <el-table-column label="ROI" min-width="100">
          <template slot-scope="{ row }">{{ formatMoney(row.avgRoi) }}</template>
        </el-table-column>
        <el-table-column prop="totalOrderNum" label="订单数" min-width="100" />
      </el-table>

      <el-table v-else :data="flatData" stripe v-loading="loading" style="width: 100%">
        <el-table-column label="代理" min-width="160">
          <template slot-scope="{ row }">
            {{ formatAgentDisplay(row.agentId) }}
          </template>
        </el-table-column>
        <el-table-column prop="advertiserId" label="广告账户ID" min-width="140" />
        <el-table-column prop="advertiserName" label="广告账户名称" min-width="180" show-overflow-tooltip />
        <el-table-column prop="storeName" label="店铺" min-width="180" show-overflow-tooltip />
        <el-table-column prop="aweMeId" label="抖音号ID" min-width="140" />
        <el-table-column prop="awemeName" label="抖音号名称" min-width="160" show-overflow-tooltip />
        <el-table-column label="消耗" min-width="120">
          <template slot-scope="{ row }">{{ formatMoney(row.totalConsumption) }}</template>
        </el-table-column>
        <el-table-column label="成交金额" min-width="120">
          <template slot-scope="{ row }">{{ formatMoney(row.totalAmount) }}</template>
        </el-table-column>
        <el-table-column label="支付金额" min-width="120">
          <template slot-scope="{ row }">{{ formatMoney(row.payAmount) }}</template>
        </el-table-column>
        <el-table-column label="ROI" min-width="100">
          <template slot-scope="{ row }">{{ formatMoney(row.avgRoi) }}</template>
        </el-table-column>
        <el-table-column prop="totalOrderNum" label="订单数" min-width="100" />
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="page.pageIndex"
          :page-size="page.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="page.total"
          @current-change="handleCurrentChange"
          @size-change="handleSizeChange"
        />
      </div>
    </div>

      </section>
    </div>

    <el-drawer
      :visible.sync="detailDrawerVisible"
      :with-header="false"
      direction="rtl"
      size="70%"
      append-to-body
      @close="handleDetailDrawerClose"
    >
      <div ref="detailDrawer" class="detail-drawer">
        <div class="detail-drawer__header">
          <div>
            <div class="detail-drawer__title">{{ detailDrawerTitle }}</div>
            <div class="detail-drawer__subtitle">{{ detailMetricHint }}</div>
          </div>
          <el-button type="text" class="detail-drawer__close" @click="detailDrawerVisible = false">
            <i class="el-icon-close"></i>
          </el-button>
        </div>

        <div class="detail-drawer__summary">
          <div class="summary-item">
            <label>总消耗</label>
            <span>{{ formatMoney(dashboardSummary.totalConsumption) }}</span>
          </div>
          <div class="summary-item">
            <label>总成交金额</label>
            <span>{{ formatMoney(dashboardSummary.totalAmount) }}</span>
          </div>
          <div class="summary-item">
            <label>整体 ROI</label>
            <span>{{ formatMoney(dashboardSummary.avgRoi) }}</span>
          </div>
          <div class="summary-item">
            <label>覆盖抖音号</label>
            <span>{{ formatCount(dashboardSummary.awemeCount) }}</span>
          </div>
          <div class="summary-item">
            <label>当前明细条数</label>
            <span>{{ formatCount(activeDetailTotal) }}</span>
          </div>
        </div>

        <div class="detail-drawer__control-card">
          <div class="detail-drawer__toolbar">
            <div class="detail-drawer__sort">
              <span class="detail-drawer__toolbar-label">排序方式</span>
              <div class="detail-drawer__metric-switch">
                <el-button
                  size="small"
                  :type="detailMetricType === 'consumption' ? 'primary' : 'default'"
                  @click="openMetricDetail('consumption')"
                >
                  消耗
                </el-button>
                <el-button
                  size="small"
                  :type="detailMetricType === 'amount' ? 'primary' : 'default'"
                  @click="openMetricDetail('amount')"
                >
                  成交金额
                </el-button>
                <el-button
                  size="small"
                  :type="detailMetricType === 'roi' ? 'primary' : 'default'"
                  @click="openMetricDetail('roi')"
                >
                  ROI
                </el-button>
              </div>
            </div>
            <div class="detail-drawer__filters">
              <el-input
                v-model.trim="detailFilterForm.keyword"
                clearable
                size="small"
                placeholder="账户 / 抖音号ID"
                class="detail-drawer__search-input"
                @keyup.enter.native="handleDetailFilterSearch"
              />
              <el-select
                v-model="detailFilterForm.accountName"
                clearable
                filterable
                size="small"
                placeholder="快速选择账户"
                class="detail-drawer__account-select"
              >
                <el-option
                  v-for="item in detailAccountOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
              <span v-if="currentDetailScopeLabel" class="detail-drawer__scope-tip">当前范围：{{ currentDetailScopeLabel }}</span>
              <el-select
                v-model="detailFilterForm.rangeMetric"
                size="small"
                class="detail-drawer__range-select"
              >
                <el-option
                  v-for="item in detailRangeMetricOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
              <span class="detail-drawer__toolbar-label">{{ detailRangeMetricLabel }}区间</span>
              <span class="detail-drawer__field-label">最小</span>
              <el-input-number
                v-model="detailFilterForm.minValue"
                :min="0"
                :precision="detailRangeMetricPrecision"
                :controls="false"
                size="small"
                class="detail-drawer__number-input"
              />
              <span class="detail-drawer__range-split">-</span>
              <span class="detail-drawer__field-label">最大</span>
              <el-input-number
                v-model="detailFilterForm.maxValue"
                :min="0"
                :precision="detailRangeMetricPrecision"
                :controls="false"
                size="small"
                class="detail-drawer__number-input"
              />
              <el-button size="small" type="primary" @click="handleDetailFilterSearch">应用</el-button>
              <el-button size="small" @click="handleDetailFilterReset">重置</el-button>
              <el-button
                size="small"
                :loading="detailExporting"
                :disabled="!activeDetailTotal"
                @click="openDetailExportDialog"
              >
                导出 CSV
              </el-button>
            </div>
          </div>
          <div class="detail-drawer__tip-row">
            <span class="detail-drawer__tip">{{ detailMetricHint }}</span>
            <span class="detail-drawer__tip detail-drawer__tip--quiet">{{ detailFilterHint }}</span>
          </div>
        </div>

        <div ref="detailTablePanel" class="detail-drawer__table-panel">
          <el-table
            :data="detailRows"
            :max-height="detailTableHeight"
            stripe
            v-loading="detailLoading"
            class="detail-drawer__table"
            @sort-change="handleDetailSortChange"
          >
            <el-table-column prop="createTime" label="时间" min-width="170" />
            <el-table-column label="广告账户" min-width="220" show-overflow-tooltip>
              <template slot-scope="{ row }">
                {{ row.advertiserName || "--" }} / {{ row.advertiserId || "--" }}
              </template>
            </el-table-column>
            <el-table-column prop="storeName" label="店铺" min-width="180" show-overflow-tooltip />
            <el-table-column label="抖音号" min-width="220" show-overflow-tooltip>
              <template slot-scope="{ row }">
                {{ row.awemeName || "--" }} / {{ row.aweMeId || "--" }}
              </template>
            </el-table-column>
            <el-table-column prop="adId" label="广告ID" min-width="140" />
            <el-table-column prop="productId" label="商品ID" min-width="140" />
            <el-table-column prop="totalConsumption" sortable="custom" label="消耗" min-width="110">
              <template slot="header">
                <span>消耗<span class="custom-sort-icon" :class="{ active: detailSort.prop === 'totalConsumption' }">{{ getSortIcon('totalConsumption', 'detail') }}</span></span>
              </template>
              <template slot-scope="{ row }">{{ formatMoney(row.totalConsumption) }}</template>
            </el-table-column>
            <el-table-column prop="totalAmount" sortable="custom" label="成交金额" min-width="120">
              <template slot="header">
                <span>成交金额<span class="custom-sort-icon" :class="{ active: detailSort.prop === 'totalAmount' }">{{ getSortIcon('totalAmount', 'detail') }}</span></span>
              </template>
              <template slot-scope="{ row }">{{ formatMoney(row.totalAmount) }}</template>
            </el-table-column>
            <el-table-column prop="avgRoi" sortable="custom" label="ROI" min-width="100">
              <template slot="header">
                <span>ROI<span class="custom-sort-icon" :class="{ active: detailSort.prop === 'avgRoi' }">{{ getSortIcon('avgRoi', 'detail') }}</span></span>
              </template>
              <template slot-scope="{ row }">{{ formatMoney(row.avgRoi) }}</template>
            </el-table-column>
            <el-table-column prop="totalOrderNum" sortable="custom" label="订单数" min-width="100">
              <template slot="header">
                <span>订单数<span class="custom-sort-icon" :class="{ active: detailSort.prop === 'totalOrderNum' }">{{ getSortIcon('totalOrderNum', 'detail') }}</span></span>
              </template>
            </el-table-column>
            <el-table-column label="详情" width="90" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" @click="openAdvertiserDetail(row)">详情</el-button>
              </template>
            </el-table-column>
          </el-table>

          <div ref="detailPagination" class="pagination-wrap detail-drawer__pagination">
            <el-pagination
              background
              layout="total, sizes, prev, pager, next, jumper"
              :current-page="detailPage.pageIndex"
              :page-size="detailPage.pageSize"
              :page-sizes="[10, 20, 50, 100]"
              :total="detailPage.total"
              @current-change="handleDetailPageChange"
              @size-change="handleDetailSizeChange"
            />
          </div>
        </div>
      </div>
    </el-drawer>

    <el-drawer
      :visible.sync="advertiserDetailDrawerVisible"
      :with-header="false"
      direction="rtl"
      size="62%"
      append-to-body
      @close="handleAdvertiserDetailClose"
    >
      <div ref="advertiserDetailDrawer" class="detail-drawer advertiser-detail-drawer">
        <div class="detail-drawer__header">
          <div>
            <div class="detail-drawer__title">{{ advertiserDetailTitle }}</div>
            <div class="detail-drawer__subtitle">{{ advertiserDetailHint }}</div>
          </div>
          <el-button type="text" class="detail-drawer__close" @click="advertiserDetailDrawerVisible = false">
            <i class="el-icon-close"></i>
          </el-button>
        </div>

        <div class="detail-drawer__summary detail-drawer__summary--compact">
          <div class="summary-item">
            <label>广告主</label>
            <span>{{ selectedAdvertiserLabel }}</span>
          </div>
          <div class="summary-item">
            <label>店铺</label>
            <span>{{ selectedAdvertiser.storeName || "--" }}</span>
          </div>
          <div class="summary-item">
            <label>当前排序</label>
            <span>{{ detailMetricLabel }}</span>
          </div>
        </div>

        <div ref="advertiserDetailTablePanel" class="detail-drawer__table-panel">
          <div ref="advertiserDetailTipRow" class="detail-drawer__tip-row detail-drawer__tip-row--inner">
            <span class="detail-drawer__tip">{{ advertiserDetailHint }}</span>
            <span class="detail-drawer__tip detail-drawer__tip--quiet">{{ detailFilterHint }}</span>
          </div>
          <el-table
            :data="advertiserDetailRows"
            :max-height="advertiserDetailTableHeight"
            stripe
            v-loading="advertiserDetailLoading"
            class="detail-drawer__table"
            @sort-change="handleAdvertiserDetailSortChange"
          >
            <el-table-column prop="createTime" label="时间" min-width="170" />
            <el-table-column label="抖音号" min-width="220" show-overflow-tooltip>
              <template slot-scope="{ row }">
                {{ row.awemeName || "--" }} / {{ row.aweMeId || "--" }}
              </template>
            </el-table-column>
            <el-table-column prop="adId" label="广告ID" min-width="140" />
            <el-table-column prop="productId" label="商品ID" min-width="140" />
            <el-table-column prop="totalConsumption" sortable="custom" label="消耗" min-width="110">
              <template slot-scope="{ row }">{{ formatMoney(row.totalConsumption) }}</template>
              <template slot="header">
                <span>消耗<span class="custom-sort-icon" :class="{ active: advertiserDetailSort.prop === 'totalConsumption' }">{{ getSortIcon('totalConsumption', 'advertiser') }}</span></span>
              </template>
            </el-table-column>
            <el-table-column prop="totalAmount" sortable="custom" label="成交金额" min-width="120">
              <template slot-scope="{ row }">{{ formatMoney(row.totalAmount) }}</template>
              <template slot="header">
                <span>成交金额<span class="custom-sort-icon" :class="{ active: advertiserDetailSort.prop === 'totalAmount' }">{{ getSortIcon('totalAmount', 'advertiser') }}</span></span>
              </template>
            </el-table-column>
            <el-table-column prop="avgRoi" sortable="custom" label="ROI" min-width="100">
              <template slot-scope="{ row }">{{ formatMoney(row.avgRoi) }}</template>
              <template slot="header">
                <span>ROI<span class="custom-sort-icon" :class="{ active: advertiserDetailSort.prop === 'avgRoi' }">{{ getSortIcon('avgRoi', 'advertiser') }}</span></span>
              </template>
            </el-table-column>
            <el-table-column prop="totalOrderNum" sortable="custom" label="订单数" min-width="100">
              <template slot="header">
                <span>订单数<span class="custom-sort-icon" :class="{ active: advertiserDetailSort.prop === 'totalOrderNum' }">{{ getSortIcon('totalOrderNum', 'advertiser') }}</span></span>
              </template>
            </el-table-column>
          </el-table>

          <div ref="advertiserDetailPagination" class="pagination-wrap detail-drawer__pagination">
            <el-pagination
              background
              layout="total, sizes, prev, pager, next, jumper"
              :current-page="advertiserDetailPage.pageIndex"
              :page-size="advertiserDetailPage.pageSize"
              :page-sizes="[10, 20, 50, 100]"
              :total="advertiserDetailPage.total"
              @current-change="handleAdvertiserDetailPageChange"
              @size-change="handleAdvertiserDetailSizeChange"
            />
          </div>
        </div>
      </div>
    </el-drawer>

    <el-dialog
      :visible.sync="exportDialogVisible"
      width="560px"
      title="选择导出内容"
      append-to-body
      custom-class="export-dialog"
    >
      <div class="export-dialog__toolbar">
        <span class="export-dialog__hint">{{ exportDialogHint }}</span>
        <div class="export-dialog__actions">
          <el-button size="mini" @click="resetExportFieldSelection">默认字段</el-button>
          <el-button size="mini" @click="toggleExportFieldSelection(true)">全选</el-button>
          <el-button size="mini" @click="toggleExportFieldSelection(false)">清空</el-button>
        </div>
      </div>
      <el-checkbox-group v-model="exportFieldSelection" class="export-dialog__group">
        <el-checkbox
          v-for="item in exportFieldOptions"
          :key="item.key"
          :label="item.key"
          class="export-dialog__checkbox"
        >
          {{ item.label }}
        </el-checkbox>
      </el-checkbox-group>
      <div slot="footer">
        <el-button @click="exportDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="detailExporting" @click="handleDetailExport">开始导出</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
const STATISTICS_COOLDOWN_MS = 5000;

function createEmptySummary() {
  return {
    totalConsumption: 0,
    totalAmount: 0,
    avgRoi: 0,
    totalOrderNum: 0,
    advertiserCount: 0,
    awemeCount: 0,
  };
}

export default {
  name: "QianchuanCostOverview",
  data() {
    return {
      loading: false,
      statisticsLoading: false,
      statisticsDisabled: false,
      statisticsSyncing: false,
      statisticsCooldownTimer: null,
      statisticsStatusTimer: null,
      isManager: false,
      viewMode: "tree",
      onlyWithConsumption: true,
      cacheTip: "",
      hasStatistics: false,
      lastStatisticsKey: "",
      latestStatisticsStatus: null,
      agentOptions: [],
      advertiserGroupOptions: [],
      advertiserOptions: [],
      treeData: [],
      flatData: [],
      dashboardSummaryLoading: false,
      dashboardSummary: createEmptySummary(),
      detailDrawerVisible: false,
      detailLoading: false,
      detailMetricType: "consumption",
      detailTableHeight: 420,
      detailRows: [],
      detailSort: { prop: "", order: "" },
      originalDetailRows: [],
      detailFilterForm: {
        keyword: "",
        accountName: "",
        rangeMetric: "consumption",
        minValue: undefined,
        maxValue: undefined,
      },
      detailExporting: false,
      exportDialogVisible: false,
      exportDialogContext: "dashboard",
      exportFieldSelection: [],
      exportFieldSelectionCache: {
        dashboard: [],
        advertiser: [],
      },
      advertiserDetailDrawerVisible: false,
      advertiserDetailLoading: false,
      advertiserDetailTableHeight: 420,
      advertiserDetailRows: [],
      advertiserDetailSort: { prop: "", order: "" },
      originalAdvertiserDetailRows: [],
      selectedAdvertiser: {
        advertiserId: undefined,
        advertiserName: "",
        storeName: "",
      },
      autoOpenDetailOnInit: false,
      handledRouteQueryToken: "",
      page: {
        pageIndex: 1,
        pageSize: 10,
        total: 0,
      },
      detailPage: {
        pageIndex: 1,
        pageSize: 10,
        total: 0,
      },
      advertiserDetailPage: {
        pageIndex: 1,
        pageSize: 10,
        total: 0,
      },
      searchForm: {
        agentId: undefined,
        groupId: undefined,
        advertiserId: undefined,
        storeName: "",
        aweMeId: "",
        dateRange: [],
      },
    };
  },
  computed: {
    statusText() {
      if (this.latestStatisticsStatus && this.latestStatisticsStatus.failed) {
        return "同步失败";
      }
      if (this.statisticsSyncing) {
        return "同步中";
      }
      if (this.hasStatistics) {
        return "缓存有效";
      }
      return "未统计";
    },
    statusClass() {
      if (this.latestStatisticsStatus && this.latestStatisticsStatus.failed) {
        return "is-failed";
      }
      if (this.statisticsSyncing) {
        return "is-syncing";
      }
      if (this.hasStatistics) {
        return "is-ready";
      }
      return "is-idle";
    },
    filteredAdvertiserOptions() {
      if (!this.searchForm.groupId) {
        return this.advertiserOptions || [];
      }
      return (this.advertiserOptions || []).filter((item) => String(item.groupId || "") === String(this.searchForm.groupId));
    },
    dashboardCards() {
      return [
        {
          metricType: "consumption",
          label: "整体消耗",
          value: this.formatMoney(this.dashboardSummary.totalConsumption),
          meta: `覆盖账户 ${this.formatCount(this.dashboardSummary.advertiserCount)}`,
        },
        {
          metricType: "amount",
          label: "整体成交金额",
          value: this.formatMoney(this.dashboardSummary.totalAmount),
          meta: `订单数 ${this.formatCount(this.dashboardSummary.totalOrderNum)}`,
        },
        {
          metricType: "roi",
          label: "支付ROI",
          value: this.formatMoney(this.dashboardSummary.avgRoi),
          meta: `覆盖抖音号 ${this.formatCount(this.dashboardSummary.awemeCount)}`,
        },
      ];
    },
    detailMetricLabel() {
      return this.resolveMetricTitle(this.detailMetricType);
    },
    detailRangeMetricOptions() {
      return [
        { value: "consumption", label: "消耗" },
        { value: "amount", label: "成交金额" },
        { value: "roi", label: "ROI" },
        { value: "orderCount", label: "订单数" },
      ];
    },
    detailRangeMetricLabel() {
      return this.resolveRangeMetricTitle(this.detailFilterForm.rangeMetric);
    },
    detailRangeMetricPrecision() {
      return this.detailFilterForm.rangeMetric === "orderCount" ? 0 : 2;
    },
    exportFieldOptions() {
      return this.getExportFieldOptions(this.exportDialogContext === "advertiser");
    },
    exportDialogHint() {
      return this.exportDialogContext === "advertiser"
        ? "当前会导出广告主二级明细，你可以自定义要包含的列。"
        : "当前会导出总览明细，你可以自定义要包含的列。";
    },
    detailDrawerTitle() {
      return "消耗 / 成交金额 / ROI 明细";
    },
    detailMetricHint() {
      return `一次性展示当前筛选范围内的消耗、成交金额和 ROI 明细，当前按${this.detailMetricLabel}倒序排序。`;
    },
    detailFilterHint() {
      if (this.detailFilterForm.minValue === undefined && this.detailFilterForm.maxValue === undefined) {
        return `未设置${this.detailRangeMetricLabel}区间时，默认返回全部明细。`;
      }
      return `当前${this.detailRangeMetricLabel}区间：${this.formatFilterMetricValue(this.detailFilterForm.minValue)} - ${this.formatFilterMetricValue(this.detailFilterForm.maxValue)}`;
    },
    activeDetailTotal() {
      return this.advertiserDetailDrawerVisible ? this.advertiserDetailPage.total : this.detailPage.total;
    },
    detailAccountOptions() {
      const options = [];
      const dedup = {};
      const appendOption = (value, label) => {
        const normalized = (value || "").trim();
        if (!normalized || dedup[normalized]) {
          return;
        }
        dedup[normalized] = true;
        options.push({
          value: normalized,
          label: label || normalized,
        });
      };
      (this.advertiserOptions || []).forEach((item) => {
        const name = item && item.name ? String(item.name).trim() : "";
        const advertiserId = item && item.advertiserId ? String(item.advertiserId) : "";
        if (name) {
          appendOption(name, advertiserId ? `${name} / ${advertiserId}` : name);
        }
      });
      (this.detailRows || []).forEach((item) => {
        appendOption(item && item.storeName, item && item.storeName);
      });
      (this.advertiserDetailRows || []).forEach((item) => {
        appendOption(item && item.storeName, item && item.storeName);
      });
      appendOption(this.selectedAdvertiser.storeName, this.selectedAdvertiser.storeName);
      return options;
    },
    currentDetailScopeLabel() {
      if (this.searchForm.advertiserId) {
        const matchedAdvertiser = (this.filteredAdvertiserOptions || []).find((item) => String(item.advertiserId) === String(this.searchForm.advertiserId))
          || (this.advertiserOptions || []).find((item) => String(item.advertiserId) === String(this.searchForm.advertiserId));
        if (matchedAdvertiser) {
          return `${matchedAdvertiser.name} / ${matchedAdvertiser.advertiserId}`;
        }
        return String(this.searchForm.advertiserId);
      }
      if (this.searchForm.groupId) {
        const matchedGroup = (this.advertiserGroupOptions || []).find((item) => String(item.id) === String(this.searchForm.groupId));
        if (matchedGroup) {
          return `分组：${matchedGroup.name}`;
        }
      }
      return "";
    },
    selectedAdvertiserLabel() {
      return `${this.selectedAdvertiser.advertiserName || "--"} / ${this.selectedAdvertiser.advertiserId || "--"}`;
    },
    advertiserDetailTitle() {
      return `${this.selectedAdvertiser.advertiserName || "广告主"}消耗详情`;
    },
    advertiserDetailHint() {
      return `直接查询连山数据库，返回当前广告主在所选日期内的全部消耗详情列表，当前按${this.detailMetricLabel}倒序排序。`;
    },
  },
  watch: {
    detailDrawerVisible(visible) {
      if (visible) {
        this.$nextTick(this.updateDrawerTableHeights);
      }
    },
    advertiserDetailDrawerVisible(visible) {
      if (visible) {
        this.$nextTick(this.updateDrawerTableHeights);
      }
    },
    "$route.query": {
      deep: true,
      handler() {
        this.handleRouteDrivenEntry();
      },
    },
  },
  created() {
    const adminInfo = JSON.parse(window.localStorage.getItem("adminInfo") || "{}");
    this.isManager = Number(adminInfo.type) === 1;
    this.loadAgentOptions();
    this.loadAdvertiserGroupOptions();
    this.loadAdvertiserOptions();
    this.initDateRange();
    this.updateDrawerTableHeights();
    window.addEventListener("resize", this.updateDrawerTableHeights);
    this.handleRouteDrivenEntry(true);
    if (!this.autoOpenDetailOnInit) {
      this.fetchDashboardSummary();
    }
  },
  activated() {
    this.handleRouteDrivenEntry();
  },
  beforeDestroy() {
    if (this.statisticsCooldownTimer) {
      clearTimeout(this.statisticsCooldownTimer);
      this.statisticsCooldownTimer = null;
    }
    if (this.statisticsStatusTimer) {
      clearInterval(this.statisticsStatusTimer);
      this.statisticsStatusTimer = null;
    }
    window.removeEventListener("resize", this.updateDrawerTableHeights);
  },
  methods: {
    agentOptionValue(item) {
      return item.id || item.agentId;
    },
    agentOptionLabel(item) {
      return item.username || item.name || item.agentName || `代理${this.agentOptionValue(item)}`;
    },
    formatAgentDisplay(agentId) {
      const agent = (this.agentOptions || []).find((item) => String(this.agentOptionValue(item)) === String(agentId));
      return agent ? this.agentOptionLabel(agent) : (agentId || "--");
    },
    initDateRange() {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const current = `${yyyy}-${mm}-${dd}`;
      this.searchForm.dateRange = [current, current];
    },
    updateDrawerTableHeights() {
      this.$nextTick(() => {
        this.detailTableHeight = this.calculateDrawerTableHeight({
          panelRef: "detailTablePanel",
          paginationRef: "detailPagination",
          minimum: 140,
          fallback: 360,
        });
        this.advertiserDetailTableHeight = this.calculateDrawerTableHeight({
          panelRef: "advertiserDetailTablePanel",
          paginationRef: "advertiserDetailPagination",
          extraRefs: ["advertiserDetailTipRow"],
          minimum: 120,
          fallback: 320,
        });
      });
    },
    calculateDrawerTableHeight(options) {
      const panel = this.$refs[options.panelRef];
      if (!panel) {
        return options.fallback;
      }
      const pagination = this.$refs[options.paginationRef];
      const extraRefs = options.extraRefs || [];
      const extraHeight = extraRefs.reduce((total, refName) => {
        const element = this.$refs[refName];
        return total + (element ? element.offsetHeight : 0);
      }, 0);
      const paginationHeight = pagination ? pagination.offsetHeight : 0;
      
      // Get padding of the panel container to compute accurate content height
      const style = window.getComputedStyle(panel);
      const paddingBottom = parseFloat(style.paddingBottom) || 0;
      const paddingTop = parseFloat(style.paddingTop) || 0;
      const contentHeight = panel.clientHeight - paddingBottom - paddingTop;
      
      const availableHeight = contentHeight - paginationHeight - extraHeight - 12;
      return Math.max(Math.floor(availableHeight), options.minimum);
    },
    applyRouteQuery(query) {
      const currentQuery = query || (this.$route && this.$route.query) || {};
      if (currentQuery.autoOpenDetail === "1") {
        this.searchForm.agentId = undefined;
        this.searchForm.groupId = undefined;
        this.searchForm.advertiserId = undefined;
        this.searchForm.storeName = "";
        this.searchForm.aweMeId = "";
        this.onlyWithConsumption = false;
        this.detailFilterForm = {
          keyword: "",
          accountName: "",
          rangeMetric: "consumption",
          minValue: undefined,
          maxValue: undefined,
        };
      }
      if (currentQuery.metricType) {
        this.detailMetricType = this.normalizeMetricType(currentQuery.metricType);
      }
      if (currentQuery.groupId !== undefined && currentQuery.groupId !== null && currentQuery.groupId !== "") {
        this.searchForm.groupId = currentQuery.groupId;
      }
      if (currentQuery.advertiserId !== undefined && currentQuery.advertiserId !== null && currentQuery.advertiserId !== "") {
        this.searchForm.advertiserId = currentQuery.advertiserId;
      }
      if (currentQuery.startDate && currentQuery.endDate) {
        this.searchForm.dateRange = [currentQuery.startDate, currentQuery.endDate];
      }
      if (currentQuery.autoOpenDetail === "1") {
        this.autoOpenDetailOnInit = true;
        this.viewMode = "flat";
      }
    },
    hasRouteDrivenQuery(query) {
      return !!(
        query &&
        (
          query.autoOpenDetail === "1" ||
          query.metricType ||
          (query.groupId !== undefined && query.groupId !== null && query.groupId !== "") ||
          (query.advertiserId !== undefined && query.advertiserId !== null && query.advertiserId !== "") ||
          (query.startDate && query.endDate)
        )
      );
    },
    buildRouteQueryToken(query) {
      if (!query) {
        return "";
      }
      return [
        query._t || "",
        query.autoOpenDetail || "",
        query.metricType || "",
        query.groupId || "",
        query.advertiserId || "",
        query.startDate || "",
        query.endDate || "",
      ].join("|");
    },
    syncDetailAccountSelectionFromScope(force = false) {
      if (!this.searchForm.advertiserId) {
        if (force) {
          this.detailFilterForm.accountName = "";
        }
        return;
      }
      const matchedAdvertiser = (this.filteredAdvertiserOptions || []).find((item) => String(item.advertiserId) === String(this.searchForm.advertiserId))
        || (this.advertiserOptions || []).find((item) => String(item.advertiserId) === String(this.searchForm.advertiserId));
      if (!matchedAdvertiser || !matchedAdvertiser.name) {
        return;
      }
      if (force || !this.detailFilterForm.accountName) {
        this.detailFilterForm.accountName = matchedAdvertiser.name;
      }
    },
    handleRouteDrivenEntry(force = false) {
      const query = (this.$route && this.$route.query) || {};
      if (!this.hasRouteDrivenQuery(query)) {
        return;
      }
      const routeToken = this.buildRouteQueryToken(query);
      if (!force && routeToken && routeToken === this.handledRouteQueryToken) {
        return;
      }
      this.applyRouteQuery(query);
      this.syncDetailAccountSelectionFromScope(true);
      this.handledRouteQueryToken = routeToken;
      if (query.autoOpenDetail === "1") {
        this.handleAdvertiserDetailClose();
        this.$nextTick(() => {
          this.openMetricDetail(this.detailMetricType, false);
        });
      }
    },
    normalizeMetricType(metricType) {
      if (metricType === "amount") {
        return "amount";
      }
      if (metricType === "roi") {
        return "roi";
      }
      return "consumption";
    },
    resolveMetricTitle(metricType) {
      const map = {
        consumption: "消耗",
        amount: "成交金额",
        roi: "ROI",
      };
      return map[this.normalizeMetricType(metricType)] || "消耗";
    },
    normalizeRangeMetric(metricType) {
      if (metricType === "amount") {
        return "amount";
      }
      if (metricType === "roi") {
        return "roi";
      }
      if (metricType === "orderCount") {
        return "orderCount";
      }
      return "consumption";
    },
    resolveRangeMetricTitle(metricType) {
      const map = {
        consumption: "消耗",
        amount: "成交金额",
        roi: "ROI",
        orderCount: "订单数",
      };
      return map[this.normalizeRangeMetric(metricType)] || "消耗";
    },
    normalizeRangeFilterValue(value) {
      if (value === null || value === undefined || value === "") {
        return undefined;
      }
      const numeric = Number(value);
      if (Number.isNaN(numeric) || numeric < 0) {
        return undefined;
      }
      if (this.detailFilterForm.rangeMetric === "orderCount") {
        return String(Math.floor(numeric));
      }
      return numeric.toFixed(2);
    },
    loadAgentOptions() {
      if (!this.isManager) {
        return;
      }
      this.$axios.post({
        url: "/agent/getAll",
        loading: false,
        success: (res) => {
          if (res.code === 0) {
            this.agentOptions = res.data.data || res.data || [];
          }
        },
      });
    },
    loadAdvertiserOptions() {
      this.$axios.post({
        url: "/oceanEngineAdvertiser/optionsByCondition",
        data: {
          pageIndex: 1,
          pageSize: 30,
        },
        loading: false,
        success: (res) => {
          if (res.code === 0) {
            const payload = res.data || {};
            this.advertiserOptions = payload.content || [];
            this.syncDetailAccountSelectionFromScope(false);
          }
        },
      });
    },
    loadAdvertiserGroupOptions() {
      this.$axios.post({
        url: "/oceanEngineAdvertiserGroup/getAll",
        loading: false,
        success: (res) => {
          if (res.code === 0) {
            this.advertiserGroupOptions = res.data || [];
          }
        },
      });
    },
    buildRequest() {
      return {
        agentId: this.searchForm.agentId || undefined,
        groupId: this.searchForm.groupId || undefined,
        advertiserId: this.searchForm.advertiserId || undefined,
        storeName: this.searchForm.storeName || undefined,
        aweMeId: this.searchForm.aweMeId || undefined,
        startTime: this.searchForm.dateRange && this.searchForm.dateRange.length ? this.searchForm.dateRange[0] : undefined,
        endTime: this.searchForm.dateRange && this.searchForm.dateRange.length ? this.searchForm.dateRange[1] : undefined,
        onlyWithConsumption: this.onlyWithConsumption ? 1 : 0,
        pageIndex: this.page.pageIndex,
        pageSize: this.page.pageSize,
      };
    },
    buildSummaryRequest() {
      const request = this.buildRequest();
      request.pageIndex = 1;
      request.pageSize = 10;
      return request;
    },
    buildDetailRequest() {
      const request = this.buildSummaryRequest();
      request.pageIndex = this.detailPage.pageIndex;
      request.pageSize = this.detailPage.pageSize;
      request.rangeMetric = this.normalizeRangeMetric(this.detailFilterForm.rangeMetric);
      request.minMetricValue = this.normalizeRangeFilterValue(this.detailFilterForm.minValue);
      request.maxMetricValue = this.normalizeRangeFilterValue(this.detailFilterForm.maxValue);
      request.minConsumption = request.rangeMetric === "consumption" ? request.minMetricValue : undefined;
      request.maxConsumption = request.rangeMetric === "consumption" ? request.maxMetricValue : undefined;
      request.sort = this.detailSort.prop || undefined;
      request.order = this.detailSort.order || undefined;
      return this.applyDetailFiltersToRequest(request, false);
    },
    buildAdvertiserDetailRequest() {
      const request = this.buildSummaryRequest();
      request.advertiserId = this.selectedAdvertiser.advertiserId || undefined;
      request.aweMeId = undefined;
      request.pageIndex = this.advertiserDetailPage.pageIndex;
      request.pageSize = this.advertiserDetailPage.pageSize;
      request.rangeMetric = this.normalizeRangeMetric(this.detailFilterForm.rangeMetric);
      request.minMetricValue = this.normalizeRangeFilterValue(this.detailFilterForm.minValue);
      request.maxMetricValue = this.normalizeRangeFilterValue(this.detailFilterForm.maxValue);
      request.minConsumption = request.rangeMetric === "consumption" ? request.minMetricValue : undefined;
      request.maxConsumption = request.rangeMetric === "consumption" ? request.maxMetricValue : undefined;
      request.sort = this.advertiserDetailSort.prop || undefined;
      request.order = this.advertiserDetailSort.order || undefined;
      return this.applyDetailFiltersToRequest(request, true);
    },
    applyDetailFiltersToRequest(request, isAdvertiserDetail) {
      const target = Object.assign({}, request || {});
      const accountName = this.detailFilterForm.accountName ? this.detailFilterForm.accountName.trim() : "";
      const keyword = this.detailFilterForm.keyword ? this.detailFilterForm.keyword.trim() : "";
      if (accountName) {
        target.storeName = accountName;
      }
      if (keyword) {
        if (/^\d+$/.test(keyword)) {
          target.aweMeId = keyword;
        } else {
          target.storeName = keyword;
        }
      }
      if (isAdvertiserDetail) {
        target.advertiserId = this.selectedAdvertiser.advertiserId || target.advertiserId;
      }
      return target;
    },
    createRequest(url, data) {
      return new Promise((resolve, reject) => {
        this.$axios.post({
          url,
          data,
          loading: false,
          success: (res) => {
            if (res.code === 0) {
              resolve(res.data);
              return;
            }
            reject(new Error(res.msg || "请求失败"));
          },
          error: (err) => {
            reject(err || new Error("请求失败"));
          },
        });
      });
    },
    buildExportRequest(pageIndex, pageSize, isAdvertiserDetail) {
      const request = isAdvertiserDetail ? this.buildAdvertiserDetailRequest() : this.buildDetailRequest();
      request.pageIndex = pageIndex;
      request.pageSize = pageSize;
      return request;
    },
    getExportFieldOptions(isAdvertiserDetail) {
      const fields = [
        {
          key: "createTime",
          label: "时间",
          defaultSelected: true,
          formatter: (row) => row.createTime || "",
        },
        {
          key: "advertiserName",
          label: "广告账户",
          defaultSelected: !isAdvertiserDetail,
          hidden: !!isAdvertiserDetail,
          formatter: (row) => row.advertiserName || "",
        },
        {
          key: "advertiserId",
          label: "广告账户ID",
          defaultSelected: !isAdvertiserDetail,
          hidden: !!isAdvertiserDetail,
          formatter: (row) => row.advertiserId || "",
        },
        {
          key: "storeName",
          label: "店铺",
          defaultSelected: !isAdvertiserDetail,
          hidden: !!isAdvertiserDetail,
          formatter: (row) => row.storeName || "",
        },
        {
          key: "awemeName",
          label: "抖音号名称",
          defaultSelected: true,
          formatter: (row) => row.awemeName || "",
        },
        {
          key: "aweMeId",
          label: "抖音号ID",
          defaultSelected: true,
          formatter: (row) => row.aweMeId || "",
        },
        {
          key: "adId",
          label: "广告ID",
          defaultSelected: true,
          formatter: (row) => row.adId || "",
        },
        {
          key: "productId",
          label: "商品ID",
          defaultSelected: true,
          formatter: (row) => row.productId || "",
        },
        {
          key: "totalConsumption",
          label: "消耗",
          defaultSelected: true,
          formatter: (row) => this.formatMoney(row.totalConsumption),
        },
        {
          key: "totalAmount",
          label: "成交金额",
          defaultSelected: true,
          formatter: (row) => this.formatMoney(row.totalAmount),
        },
        {
          key: "avgRoi",
          label: "ROI",
          defaultSelected: true,
          formatter: (row) => this.formatMoney(row.avgRoi),
        },
        {
          key: "totalOrderNum",
          label: "订单数",
          defaultSelected: true,
          formatter: (row) => this.formatCount(row.totalOrderNum),
        },
      ];
      return fields.filter((item) => !item.hidden);
    },
    getDefaultExportFieldSelection(isAdvertiserDetail) {
      return this.getExportFieldOptions(isAdvertiserDetail)
        .filter((item) => item.defaultSelected)
        .map((item) => item.key);
    },
    openDetailExportDialog() {
      const context = this.advertiserDetailDrawerVisible && this.selectedAdvertiser.advertiserId ? "advertiser" : "dashboard";
      const cachedSelection = this.exportFieldSelectionCache[context] || [];
      this.exportDialogContext = context;
      this.exportFieldSelection = cachedSelection.length
        ? cachedSelection.slice()
        : this.getDefaultExportFieldSelection(context === "advertiser");
      this.exportDialogVisible = true;
    },
    resetExportFieldSelection() {
      this.exportFieldSelection = this.getDefaultExportFieldSelection(this.exportDialogContext === "advertiser");
    },
    toggleExportFieldSelection(selectAll) {
      this.exportFieldSelection = selectAll
        ? this.exportFieldOptions.map((item) => item.key)
        : [];
    },
    buildExportRows(rows, isAdvertiserDetail, selectedFields) {
      const fieldOptions = this.getExportFieldOptions(isAdvertiserDetail);
      const fieldMap = fieldOptions.reduce((result, item) => {
        result[item.key] = item;
        return result;
      }, {});
      const orderedFields = (selectedFields || []).filter((key) => fieldMap[key]);
      return (rows || []).map((row) => {
        return orderedFields.reduce((result, key) => {
          const field = fieldMap[key];
          result[field.label] = field.formatter(row);
          return result;
        }, {});
      });
    },
    buildCsvContent(rows) {
      if (!rows || !rows.length) {
        return "";
      }
      const headers = Object.keys(rows[0]);
      const escapeCell = (value) => {
        const raw = value === null || value === undefined ? "" : String(value);
        return `"${raw.replace(/"/g, '""')}"`;
      };
      const lines = [headers.map(escapeCell).join(",")];
      rows.forEach((row) => {
        lines.push(headers.map((header) => escapeCell(row[header])).join(","));
      });
      return "\uFEFF" + lines.join("\n");
    },
    triggerCsvDownload(content, fileName) {
      const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    async handleDetailExport() {
      const isAdvertiserDetail = !!(this.advertiserDetailDrawerVisible && this.selectedAdvertiser.advertiserId);
      const selectedFields = (this.exportFieldSelection || []).slice();
      if (!selectedFields.length) {
        this.$message.warning("请至少选择一个导出字段");
        return;
      }
      const baseUrl = isAdvertiserDetail
        ? `/qianchuanCostOverview/dashboardAdvertiserDetail/${this.selectedAdvertiser.advertiserId}/${this.detailMetricType}`
        : `/qianchuanCostOverview/dashboardDetail/${this.detailMetricType}`;
      const pageSize = 500;
      let pageIndex = 1;
      let total = 0;
      let rows = [];
      this.detailExporting = true;
      try {
        this.exportFieldSelectionCache[isAdvertiserDetail ? "advertiser" : "dashboard"] = selectedFields.slice();
        do {
          const data = await this.createRequest(baseUrl, this.buildExportRequest(pageIndex, pageSize, isAdvertiserDetail));
          const content = (data && data.content) || [];
          total = data && data.totalElements ? data.totalElements : content.length;
          rows = rows.concat(content);
          pageIndex += 1;
        } while (rows.length < total);
        if (!rows.length) {
          this.$message.warning("当前没有可导出的明细数据");
          return;
        }
        const exportRows = this.buildExportRows(rows, isAdvertiserDetail, selectedFields);
        const csvContent = this.buildCsvContent(exportRows);
        const fileName = isAdvertiserDetail
          ? `广告主明细-${this.detailMetricLabel}-${Date.now()}.csv`
          : `总览明细-${this.detailMetricLabel}-${Date.now()}.csv`;
        this.triggerCsvDownload(csvContent, fileName);
        this.exportDialogVisible = false;
        this.$message.success(`已导出 ${rows.length} 条明细`);
      } catch (e) {
        this.$message.error("导出 CSV 失败");
      } finally {
        this.detailExporting = false;
      }
    },
    buildStatisticsKey() {
      return JSON.stringify({
        viewMode: this.viewMode,
        agentId: this.searchForm.agentId || "",
        groupId: this.searchForm.groupId || "",
        advertiserId: this.searchForm.advertiserId || "",
        storeName: this.searchForm.storeName || "",
        aweMeId: this.searchForm.aweMeId || "",
        startTime: this.searchForm.dateRange && this.searchForm.dateRange.length ? this.searchForm.dateRange[0] : "",
        endTime: this.searchForm.dateRange && this.searchForm.dateRange.length ? this.searchForm.dateRange[1] : "",
        onlyWithConsumption: this.onlyWithConsumption ? 1 : 0,
      });
    },
    startStatisticsCooldown() {
      this.statisticsDisabled = true;
      if (this.statisticsCooldownTimer) {
        clearTimeout(this.statisticsCooldownTimer);
      }
      this.statisticsCooldownTimer = setTimeout(() => {
        this.statisticsDisabled = false;
        this.statisticsCooldownTimer = null;
      }, STATISTICS_COOLDOWN_MS);
    },
    markStatisticsDirty() {
      this.hasStatistics = false;
      this.statisticsSyncing = false;
      this.cacheTip = "";
      this.lastStatisticsKey = "";
      this.latestStatisticsStatus = null;
      this.treeData = [];
      this.flatData = [];
      this.page.total = 0;
      if (this.statisticsStatusTimer) {
        clearInterval(this.statisticsStatusTimer);
        this.statisticsStatusTimer = null;
      }
    },
    refreshDashboardPanels() {
      this.fetchDashboardSummary();
      if (this.detailDrawerVisible) {
        this.fetchDashboardDetail();
      }
      if (this.advertiserDetailDrawerVisible) {
        this.fetchAdvertiserDetail();
      }
    },
    fetchDashboardSummary() {
      this.dashboardSummaryLoading = true;
      this.$axios.post({
        url: "/qianchuanCostOverview/dashboardSummary",
        data: this.buildSummaryRequest(),
        loading: false,
        success: (res) => {
          this.dashboardSummaryLoading = false;
          if (res.code === 0 && res.data) {
            this.dashboardSummary = Object.assign(createEmptySummary(), res.data);
            this.updateDrawerTableHeights();
            return;
          }
          this.dashboardSummary = createEmptySummary();
        },
        error: () => {
          this.dashboardSummaryLoading = false;
          this.dashboardSummary = createEmptySummary();
        },
      });
    },
    fetchDashboardDetail() {
      if (!this.detailDrawerVisible) {
        return;
      }
      this.detailLoading = true;
      this.$axios.post({
        url: `/qianchuanCostOverview/dashboardDetail/${this.detailMetricType}`,
        data: this.buildDetailRequest(),
        loading: false,
        success: (res) => {
          this.detailLoading = false;
          if (res.code !== 0 || !res.data) {
            this.$message.error(res.msg || "加载首页明细失败");
            return;
          }
          this.originalDetailRows = res.data.content || [];
          this.detailRows = res.data.content || [];
          this.detailPage.total = res.data.totalElements || 0;
          this.updateDrawerTableHeights();
        },
        error: () => {
          this.detailLoading = false;
          this.$message.error("加载首页明细失败");
        },
      });
    },
    fetchAdvertiserDetail() {
      if (!this.advertiserDetailDrawerVisible || !this.selectedAdvertiser.advertiserId) {
        return;
      }
      this.advertiserDetailLoading = true;
      this.$axios.post({
        url: `/qianchuanCostOverview/dashboardAdvertiserDetail/${this.selectedAdvertiser.advertiserId}/${this.detailMetricType}`,
        data: this.buildAdvertiserDetailRequest(),
        loading: false,
        success: (res) => {
          this.advertiserDetailLoading = false;
          if (res.code !== 0 || !res.data) {
            this.$message.error(res.msg || "加载广告主明细失败");
            return;
          }
          this.originalAdvertiserDetailRows = res.data.content || [];
          this.advertiserDetailRows = res.data.content || [];
          this.advertiserDetailPage.total = res.data.totalElements || 0;
          this.updateDrawerTableHeights();
        },
        error: () => {
          this.advertiserDetailLoading = false;
          this.$message.error("加载广告主明细失败");
        },
      });
    },
    openMetricDetail(metricType, resetPage = true) {
      this.detailMetricType = this.normalizeMetricType(metricType);
      this.detailSort = {
        prop: '',
        order: ''
      };
      if (resetPage) {
        this.detailPage.pageIndex = 1;
      }
      this.detailDrawerVisible = true;
      this.fetchDashboardSummary();
      this.fetchDashboardDetail();
      if (this.advertiserDetailDrawerVisible) {
        this.advertiserDetailPage.pageIndex = 1;
        this.advertiserDetailSort = {
          prop: '',
          order: ''
        };
        this.fetchAdvertiserDetail();
      }
    },
    openAdvertiserDetail(row, resetPage = true) {
      if (!row || !row.advertiserId) {
        this.$message.warning("当前行缺少广告主信息");
        return;
      }
      this.selectedAdvertiser = {
        advertiserId: row.advertiserId,
        advertiserName: row.advertiserName || String(row.advertiserId),
        storeName: row.storeName || "",
      };
      if (resetPage) {
        this.advertiserDetailPage.pageIndex = 1;
      }
      this.advertiserDetailSort = {
        prop: '',
        order: ''
      };
      this.advertiserDetailDrawerVisible = true;
      this.fetchAdvertiserDetail();
    },
    handleDetailPageChange(pageIndex) {
      this.detailPage.pageIndex = pageIndex;
      this.fetchDashboardDetail();
    },
    handleDetailFilterSearch() {
      const minValue = this.normalizeRangeFilterValue(this.detailFilterForm.minValue);
      const maxValue = this.normalizeRangeFilterValue(this.detailFilterForm.maxValue);
      if (minValue !== undefined && maxValue !== undefined && Number(minValue) > Number(maxValue)) {
        this.$message.warning(`最小${this.detailRangeMetricLabel}不能大于最大${this.detailRangeMetricLabel}`);
        return;
      }
      this.detailPage.pageIndex = 1;
      this.fetchDashboardDetail();
      if (this.advertiserDetailDrawerVisible) {
        this.advertiserDetailPage.pageIndex = 1;
        this.fetchAdvertiserDetail();
      }
    },
    handleDetailFilterReset() {
      this.detailFilterForm = {
        keyword: "",
        accountName: "",
        rangeMetric: "consumption",
        minValue: undefined,
        maxValue: undefined,
      };
      this.syncDetailAccountSelectionFromScope(true);
      this.handleDetailFilterSearch();
    },
    handleDetailSizeChange(pageSize) {
      this.detailPage.pageSize = pageSize;
      this.detailPage.pageIndex = 1;
      this.fetchDashboardDetail();
    },
    handleAdvertiserDetailPageChange(pageIndex) {
      this.advertiserDetailPage.pageIndex = pageIndex;
      this.fetchAdvertiserDetail();
    },
    handleAdvertiserDetailSizeChange(pageSize) {
      this.advertiserDetailPage.pageSize = pageSize;
      this.advertiserDetailPage.pageIndex = 1;
      this.fetchAdvertiserDetail();
    },
    handleDetailDrawerClose() {
      this.detailDrawerVisible = false;
      this.detailSort = { prop: "", order: "" };
      this.originalDetailRows = [];
      this.handleAdvertiserDetailClose();
    },
    handleAdvertiserDetailClose() {
      this.advertiserDetailDrawerVisible = false;
      this.advertiserDetailRows = [];
      this.advertiserDetailSort = { prop: "", order: "" };
      this.originalAdvertiserDetailRows = [];
      this.advertiserDetailPage.pageIndex = 1;
      this.advertiserDetailPage.total = 0;
      this.selectedAdvertiser = {
        advertiserId: undefined,
        advertiserName: "",
        storeName: "",
      };
    },
    getSortIcon(prop, tableType) {
      const sortState = tableType === "advertiser" ? this.advertiserDetailSort : this.detailSort;
      if (sortState.prop === prop) {
        if (sortState.order === "ascending") {
          return "↑";
        } else if (sortState.order === "descending") {
          return "↓";
        }
      }
      return "-";
    },
    handleDetailSortChange({ prop, order }) {
      this.detailSort = { prop: prop || "", order: order || "" };
      this.applyLocalSort("detail");
    },
    handleAdvertiserDetailSortChange({ prop, order }) {
      this.advertiserDetailSort = { prop: prop || "", order: order || "" };
      this.applyLocalSort("advertiser");
    },
    applyLocalSort(tableType) {
      if (tableType === "detail") {
        const { prop, order } = this.detailSort;
        if (!prop || !order) {
          this.detailRows = [...this.originalDetailRows];
        } else {
          this.detailRows = this.sortArray([...this.originalDetailRows], prop, order);
        }
      } else if (tableType === "advertiser") {
        const { prop, order } = this.advertiserDetailSort;
        if (!prop || !order) {
          this.advertiserDetailRows = [...this.originalAdvertiserDetailRows];
        } else {
          this.advertiserDetailRows = this.sortArray([...this.originalAdvertiserDetailRows], prop, order);
        }
      }
      this.updateDrawerTableHeights();
    },
    sortArray(array, prop, order) {
      const isAsc = order === "ascending";
      return array.sort((a, b) => {
        const valA = Number(a[prop]) || 0;
        const valB = Number(b[prop]) || 0;
        if (valA === valB) return 0;
        return isAsc ? valA - valB : valB - valA;
      });
    },
    updateStatisticsView(status) {
      this.latestStatisticsStatus = status || null;
      this.statisticsSyncing = !!(status && status.syncing);
      this.hasStatistics = !!(status && (status.ready || status.syncing || (status.totalElements || 0) > 0));
      if (!status) {
        this.cacheTip = "";
        return;
      }
      if (status.failed) {
        this.cacheTip = `同步失败，已处理 ${status.processedCount || 0} / ${status.totalCount || 0}，当前可查看 ${status.totalElements || 0} 条，失败 ${status.failedCount || 0}`;
        return;
      }
      if (status.syncing) {
        this.cacheTip = `同步中，已处理 ${status.processedCount || 0} / ${status.totalCount || 0}，当前可查看 ${status.totalElements || 0} 条，失败 ${status.failedCount || 0}`;
        return;
      }
      if (status.ready) {
        this.cacheTip = `当前统计 ${status.totalElements || 0} 条，缓存剩余约 ${status.cacheExpireInSeconds || 0} 秒`;
        return;
      }
      this.cacheTip = "";
    },
    fetchStatisticsStatus(callback) {
      this.$axios.post({
        url: `/qianchuanCostOverview/statisticsStatus/${this.viewMode}`,
        data: this.buildRequest(),
        loading: false,
        success: (res) => {
          if (res.code !== 0 || !res.data) {
            callback && callback(null);
            return;
          }
          this.updateStatisticsView(res.data);
          callback && callback(res.data);
        },
        error: () => {
          callback && callback(null);
        },
      });
    },
    startStatusPolling() {
      if (this.statisticsStatusTimer) {
        clearInterval(this.statisticsStatusTimer);
      }
      this.statisticsStatusTimer = setInterval(() => {
        this.fetchStatisticsStatus((status) => {
          if (!status) {
            return;
          }
          this.updateStatisticsView(status);
          if (status.syncing) {
            if (this.lastStatisticsKey === this.buildStatisticsKey()) {
              this.fetchData();
            }
            return;
          }
          if (this.lastStatisticsKey === this.buildStatisticsKey()
            && (status.ready || (status.failed && (status.totalElements || 0) > 0))) {
            this.fetchData();
          }
          if ((status.ready || status.failed) && this.statisticsStatusTimer) {
            clearInterval(this.statisticsStatusTimer);
            this.statisticsStatusTimer = null;
          }
        });
      }, 2000);
    },
    fetchData() {
      this.loading = true;
      const url = this.viewMode === "tree" ? "/qianchuanCostOverview/summaryTree" : "/qianchuanCostOverview/flatList";
      this.$axios.post({
        url,
        data: this.buildRequest(),
        success: (res) => {
          this.loading = false;
          if (res.code !== 0 || !res.data) {
            this.$message.error(res.msg || "加载消耗总览失败");
            return;
          }
          const content = res.data.content || [];
          if (this.viewMode === "tree") {
            this.treeData = content;
          } else {
            this.flatData = content;
          }
          this.page.total = res.data.totalElements || 0;
          this.fetchDashboardSummary();
        },
        error: () => {
          this.loading = false;
          this.$message.error("加载消耗总览失败");
        },
      });
    },
    handleStatistics() {
      if (this.statisticsLoading || this.statisticsDisabled || this.statisticsSyncing) {
        return;
      }
      this.statisticsLoading = true;
      this.statisticsSyncing = true;
      this.cacheTip = "正在提交同步任务，你可以继续进行其他操作。";
      this.page.pageIndex = 1;
      this.startStatisticsCooldown();
      const statisticsKey = this.buildStatisticsKey();
      this.$axios.post({
        url: `/qianchuanCostOverview/statistics/${this.viewMode}`,
        data: this.buildRequest(),
        loading: false,
        success: (res) => {
          this.statisticsLoading = false;
          if (res.code !== 0 || !res.data) {
            this.statisticsSyncing = false;
            this.$message.error(res.msg || "数据同步失败");
            return;
          }
          this.lastStatisticsKey = statisticsKey;
          this.updateStatisticsView(res.data);
          this.fetchData();
          if (res.data.syncing) {
            this.$message.success(res.data.message || "已开始同步");
            this.startStatusPolling();
            return;
          }
          if (res.data.failed) {
            this.$message.warning(res.data.message || "数据同步失败，已返回当前可用数据");
            return;
          }
          this.$message.success(res.data.message || "数据同步完成");
        },
        error: () => {
          this.statisticsLoading = false;
          this.statisticsSyncing = false;
          this.$message.error("数据同步失败");
        },
      });
    },
    handleSearch(resetPage = true) {
      if (resetPage) {
        this.page.pageIndex = 1;
      }
      this.refreshDashboardPanels();
      if (!this.lastStatisticsKey || this.lastStatisticsKey !== this.buildStatisticsKey()) {
        this.$message.warning("当前筛选条件已变更，请先点击“数据同步”");
        return;
      }
      this.fetchStatisticsStatus((status) => {
        if (!status) {
          this.$message.error("状态查询失败");
          return;
        }
        this.updateStatisticsView(status);
        if (status.syncing || status.ready || (status.failed && (status.totalElements || 0) > 0)) {
          if (status.syncing) {
            this.startStatusPolling();
          }
          this.fetchData();
          return;
        }
        this.$message.warning(status.failed ? "同步失败，请重新点击“数据同步”" : "当前统计结果已失效，请重新点击“数据同步”");
      });
    },
    handleReset() {
      this.searchForm = {
        agentId: undefined,
        groupId: undefined,
        advertiserId: undefined,
        storeName: "",
        aweMeId: "",
        dateRange: [],
      };
      this.onlyWithConsumption = true;
      this.page.pageIndex = 1;
      this.detailPage.pageIndex = 1;
      this.detailPage.total = 0;
      this.detailRows = [];
      this.detailSort = { prop: "", order: "" };
      this.originalDetailRows = [];
      this.detailFilterForm = {
        keyword: "",
        accountName: "",
        rangeMetric: "consumption",
        minValue: undefined,
        maxValue: undefined,
      };
      this.detailDrawerVisible = false;
      this.handleAdvertiserDetailClose();
      this.initDateRange();
      this.markStatisticsDirty();
      this.dashboardSummary = createEmptySummary();
      this.fetchDashboardSummary();
    },
    handleGroupChange() {
      if (this.searchForm.advertiserId && !this.filteredAdvertiserOptions.find((item) => String(item.advertiserId) === String(this.searchForm.advertiserId))) {
        this.searchForm.advertiserId = undefined;
      }
    },
    handleViewChange() {
      this.page.pageIndex = 1;
      this.treeData = [];
      this.flatData = [];
      this.page.total = 0;
      this.lastStatisticsKey = "";
      this.cacheTip = "";
      this.hasStatistics = false;
      this.statisticsSyncing = false;
      this.latestStatisticsStatus = null;
      if (this.statisticsStatusTimer) {
        clearInterval(this.statisticsStatusTimer);
        this.statisticsStatusTimer = null;
      }
      this.fetchStatisticsStatus((status) => {
        if (!status) {
          this.$message.info("已切换视图，请先点击「数据同步」");
          return;
        }
        this.updateStatisticsView(status);
        if (status.ready || (status.syncing && (status.totalElements || 0) > 0)) {
          this.lastStatisticsKey = this.buildStatisticsKey();
          this.fetchData();
          if (status.syncing) {
            this.startStatusPolling();
          }
          return;
        }
        this.$message.info(
          this.viewMode === "flat"
            ? "全量平铺按抖音号展开，需单独同步；若分层有数据而平铺为空，多为账户下未绑定抖音号。"
            : "已切换为分层汇总，请先点击「数据同步」"
        );
      });
    },
    handleCurrentChange(pageIndex) {
      this.page.pageIndex = pageIndex;
      if (!this.lastStatisticsKey || this.lastStatisticsKey !== this.buildStatisticsKey()) {
        this.$message.warning("请先重新点击“数据同步”");
        return;
      }
      this.handleSearch(false);
    },
    handleSizeChange(pageSize) {
      this.page.pageSize = pageSize;
      this.page.pageIndex = 1;
      if (!this.lastStatisticsKey || this.lastStatisticsKey !== this.buildStatisticsKey()) {
        this.$message.warning("请先重新点击“数据同步”");
        return;
      }
      this.handleSearch(false);
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === "") {
        return "--";
      }
      return Number(value).toFixed(2);
    },
    formatFilterMetricValue(value) {
      if (value === null || value === undefined || value === "") {
        return "不限";
      }
      if (this.detailFilterForm.rangeMetric === "orderCount") {
        return String(Math.floor(Number(value)));
      }
      return Number(value).toFixed(2);
    },
    formatCount(value) {
      if (value === null || value === undefined || value === "") {
        return "0";
      }
      return String(value);
    },
    formatNodeType(value) {
      const map = {
        advertiser: "广告账户",
        store: "店铺",
        aweme: "抖音号",
      };
      return map[value] || value || "--";
    },
  },
};
</script>

<style scoped>
.overview-page {
  padding: 32px;
  background: #f5f7fa;
  min-height: calc(100vh - 48px);
  box-sizing: border-box;
}

.overview-shell {
  display: flex;
  align-items: flex-start;
  gap: 28px;
}

.overview-sidebar {
  position: sticky;
  top: 24px;
  flex: 0 0 320px;
  max-width: 320px;
}

.overview-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 2px 0 10px;
}

.overview-hero {
  padding: 24px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e7ebf3;
  box-shadow: 0 10px 30px rgba(31, 35, 41, 0.08);
}

.page-header {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 18px;
  margin-bottom: 24px;
}

.page-header__main {
  min-width: 0;
}

.page-eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: #ecf5ff;
  color: #3a7afe;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.page-title {
  margin-top: 14px;
  font-size: clamp(28px, 2.4vw, 32px);
  font-weight: 800;
  color: #303133;
  line-height: 1.25;
}

.page-subtitle {
  margin-top: 12px;
  font-size: 14px;
  line-height: 1.8;
  color: #909399;
}

.page-header__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.page-tag {
  display: inline-flex;
  align-items: center;
  padding: 7px 12px;
  border-radius: 999px;
  background: #f4f8fd;
  border: 1px solid #e1e8f0;
  color: #606266;
  font-size: 12px;
  font-weight: 700;
}

.page-tag--warn {
  background: #fff8ef;
  border-color: #f6d9a7;
  color: #c77b18;
}

.header-actions {
  padding: 14px;
  border-radius: 12px;
  background: #f7f9fc;
  border: 1px solid #e7ebf3;
}

.header-actions ::v-deep .el-radio-group {
  display: flex;
  width: 100%;
}

.header-actions ::v-deep .el-radio-button {
  flex: 1;
}

.header-actions ::v-deep .el-radio-button__inner {
  width: 100%;
  padding: 11px 14px;
  border-radius: 10px;
  border: 0;
  background: transparent;
  color: #606266;
  font-weight: 700;
  box-shadow: none;
  transition: all 0.2s ease;
}

.header-actions ::v-deep .el-radio-button:first-child .el-radio-button__inner,
.header-actions ::v-deep .el-radio-button:last-child .el-radio-button__inner {
  border-radius: 10px;
}

.header-actions ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: #3a7afe;
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(58, 122, 254, 0.22);
}

.dashboard-card-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.dashboard-card {
  position: relative;
  min-height: 124px;
  padding: 20px 20px 18px;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f7faff 100%);
  box-shadow: 0 8px 24px rgba(31, 35, 41, 0.06);
  border: 1px solid #e8edf5;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.dashboard-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, #71a5ff 0%, #3a7afe 100%);
  opacity: 0.9;
}

.dashboard-card::after {
  content: "";
  position: absolute;
  right: -12px;
  bottom: -18px;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(58, 122, 254, 0.16) 0%, rgba(58, 122, 254, 0) 72%);
}

.dashboard-card:nth-child(2)::before {
  background: linear-gradient(90deg, #ffbe76 0%, #ff9954 100%);
}

.dashboard-card:nth-child(2)::after {
  background: radial-gradient(circle, rgba(255, 168, 78, 0.18) 0%, rgba(255, 168, 78, 0) 72%);
}

.dashboard-card:nth-child(3)::before {
  background: linear-gradient(90deg, #58c4ff 0%, #3d8bff 100%);
}

.dashboard-card:hover,
.dashboard-card.active {
  transform: translateY(-2px);
  border-color: #3a7afe;
  box-shadow: 0 14px 28px rgba(58, 122, 254, 0.14);
}

.dashboard-card__label {
  position: relative;
  z-index: 1;
  font-size: 14px;
  font-weight: 700;
  color: #909399;
}

.dashboard-card__value {
  position: relative;
  z-index: 1;
  margin-top: 20px;
  font-size: clamp(28px, 2vw, 34px);
  font-weight: 800;
  color: #303133;
  line-height: 1.1;
  letter-spacing: -0.03em;
}

.dashboard-card__meta {
  position: relative;
  z-index: 1;
  margin-top: 14px;
  font-size: 12px;
  font-weight: 700;
  color: #7d8aa5;
}

.filter-card,
.table-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px 26px;
  border: 1px solid #e7ebf3;
  box-shadow: 0 10px 30px rgba(31, 35, 41, 0.08);
}

.filter-card__header,
.table-card__header {
  margin-bottom: 18px;
}

.filter-card__title,
.table-card__title {
  font-size: 20px;
  font-weight: 800;
  color: #303133;
  line-height: 1.3;
}

.filter-card__subtitle,
.table-card__subtitle {
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.7;
  color: #909399;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}

.filter-row__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-left: auto;
}

.filter-item {
  width: 210px;
}

.date-range {
  width: 330px;
}

.filter-check {
  display: inline-flex;
  align-items: center;
  min-height: 42px;
  padding: 0 2px;
  color: #606266;
}

.overview-page ::v-deep .el-input__inner,
.overview-page ::v-deep .el-range-editor.el-input__inner,
.overview-page ::v-deep .el-date-editor .el-range-input,
.overview-page ::v-deep .el-select .el-input__inner {
  height: 42px;
  border-radius: 10px;
  border-color: #dcdfe6;
  background: #ffffff;
  color: #303133;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.overview-page ::v-deep .el-range-editor.el-input__inner {
  padding: 0 14px;
}

.overview-page ::v-deep .el-input__inner:focus,
.overview-page ::v-deep .el-range-editor.is-active,
.overview-page ::v-deep .el-select .el-input.is-focus .el-input__inner {
  border-color: #3a7afe;
  box-shadow: 0 0 0 3px rgba(58, 122, 254, 0.12);
}

.overview-page ::v-deep .el-checkbox__label {
  font-size: 13px;
  color: #606266;
}

.overview-page ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner,
.overview-page ::v-deep .el-checkbox__input.is-indeterminate .el-checkbox__inner {
  background-color: #3a7afe;
  border-color: #3a7afe;
}

.overview-page ::v-deep .el-button {
  min-height: 42px;
  padding: 0 18px;
  border-radius: 10px;
  font-weight: 700;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, background-color 0.18s ease, color 0.18s ease;
}

.overview-page ::v-deep .el-button:hover {
  transform: translateY(-1px);
}

.overview-page ::v-deep .el-button--primary {
  background: #3a7afe;
  border-color: #3a7afe;
  box-shadow: 0 10px 22px rgba(58, 122, 254, 0.2);
}

.overview-page ::v-deep .el-button--primary:hover,
.overview-page ::v-deep .el-button--primary:focus {
  background: #2f6ff0;
  border-color: #2f6ff0;
  box-shadow: 0 12px 24px rgba(47, 111, 240, 0.24);
}

.overview-page ::v-deep .el-button:not(.el-button--primary):hover,
.overview-page ::v-deep .el-button:not(.el-button--primary):focus {
  color: #3a7afe;
  border-color: #bfd3ff;
  background: #f5f9ff;
}

.cache-tip {
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 16px 18px;
  border-radius: 12px;
  border: 1px solid #ebf0f6;
  background: #f8fafc;
  color: #909399;
  font-size: 13px;
}

.cache-tip__headline {
  display: block;
  width: 100%;
  color: #606266;
  font-weight: 700;
  line-height: 1.8;
}

.cache-tip__headline--warn {
  color: #c77b18;
}

.cache-tip__meta {
  display: inline-flex;
  align-items: center;
  color: #3a7afe;
  font-weight: 600;
}

.cache-tip__status {
  padding: 4px 12px;
  border-radius: 999px;
  font-weight: 600;
}

.cache-tip__status.is-idle {
  color: #8a6d3b;
  background: #fff7e6;
}

.cache-tip__status.is-syncing {
  color: #0f5ad1;
  background: #e8f3ff;
}

.cache-tip__status.is-ready {
  color: #1f7a36;
  background: #edf9f0;
}

.cache-tip__status.is-failed {
  color: #b54708;
  background: #fff1e8;
}

.pagination-wrap {
  padding-top: 24px;
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

.pagination-wrap ::v-deep .el-pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}

.pagination-wrap ::v-deep .el-pagination.is-background .btn-next,
.pagination-wrap ::v-deep .el-pagination.is-background .btn-prev,
.pagination-wrap ::v-deep .el-pagination.is-background .el-pager li {
  min-width: 34px;
  height: 34px;
  line-height: 34px;
  border-radius: 8px;
}

.pagination-wrap ::v-deep .el-pagination.is-background .el-pager li:not(.disabled).active {
  background-color: #3a7afe;
}

.pagination-wrap ::v-deep .el-pagination__total,
.pagination-wrap ::v-deep .el-pagination__sizes,
.pagination-wrap ::v-deep .el-pagination__jump {
  color: #606266;
}

.table-card ::v-deep .el-table {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #edf1f6;
}

.table-card ::v-deep .el-table::before,
.table-card ::v-deep .el-table__fixed::before,
.table-card ::v-deep .el-table__fixed-right::before {
  display: none;
}

.table-card ::v-deep th.el-table__cell {
  padding: 14px 0;
  background: #f7f9fc;
  color: #606266;
  font-weight: 700;
  border-bottom: 1px solid #ebeef5;
}

.table-card ::v-deep td.el-table__cell {
  padding: 13px 0;
  color: #303133;
  border-bottom: 1px solid #edf2f7;
}

.table-card ::v-deep .el-table--striped .el-table__body tr.el-table__row--striped td.el-table__cell {
  background: #fafbfd;
}

.table-card ::v-deep .el-table__body tr:hover > td.el-table__cell {
  background: #f5f9ff;
}

.table-card ::v-deep .el-table__empty-text {
  color: #909399;
}

::v-deep .el-drawer__body {
  height: 100%;
  overflow: hidden;
}

.detail-drawer {
  height: 100%;
  background: linear-gradient(180deg, #f5f8fd 0%, #f8fbff 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overscroll-behavior: contain;
}

.detail-drawer__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 22px 28px 14px;
  background: rgba(255, 255, 255, 0.92);
  border-bottom: 1px solid #e9eff7;
  backdrop-filter: blur(12px);
}

.detail-drawer__title {
  font-size: clamp(20px, 2vw, 24px);
  font-weight: 700;
  color: #1d2b45;
  letter-spacing: 0.01em;
  line-height: 1.35;
}

.detail-drawer__subtitle {
  margin-top: 6px;
  max-width: 860px;
  font-size: clamp(12px, 1.1vw, 13px);
  line-height: 1.6;
  color: #7c8aa5;
}

.detail-drawer__close {
  font-size: 18px;
}

.detail-drawer__summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 14px;
  padding: 18px 28px;
}

.detail-drawer__summary--compact {
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.summary-item {
  padding: 16px 18px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  border: 1px solid #eaf0f8;
  box-shadow: 0 10px 24px rgba(31, 55, 95, 0.05);
}

.summary-item label {
  display: block;
  font-size: 12px;
  color: #8b97ac;
}

.summary-item span {
  display: block;
  margin-top: 12px;
  font-size: clamp(18px, 1.8vw, 28px);
  font-weight: 700;
  color: #1d2b45;
  line-height: 1.35;
  word-break: break-word;
}

.detail-drawer__summary--compact .summary-item span {
  font-size: 18px;
  line-height: 1.5;
}

.detail-drawer__control-card {
  margin: 0 28px 18px;
  padding: 16px 18px 14px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #e9eff7;
  box-shadow: 0 10px 28px rgba(31, 55, 95, 0.06);
}

.detail-drawer__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 14px 18px;
}

.detail-drawer__sort,
.detail-drawer__filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.detail-drawer__sort {
  flex: 0 1 auto;
}

.detail-drawer__filters {
  flex: 1 1 520px;
}

.detail-drawer__toolbar-label {
  font-size: 12px;
  font-weight: 600;
  color: #8b97ac;
}

.detail-drawer__field-label {
  font-size: 12px;
  color: #8b97ac;
}

.detail-drawer__metric-switch {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.detail-drawer__search-input {
  width: 220px;
}

.detail-drawer__account-select {
  width: 220px;
}

.detail-drawer__range-select {
  width: 132px;
}

.detail-drawer__number-input {
  width: 136px;
}

.detail-drawer__range-split {
  color: #8b97ac;
}

.detail-drawer__tip-row {
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.detail-drawer__tip-row--inner {
  margin: 0 0 12px;
}

.detail-drawer__tip {
  font-size: 13px;
  color: #5f6f89;
}

.detail-drawer__tip--quiet {
  color: #8b97ac;
}

.detail-drawer__table {
  width: 100%;
}

.detail-drawer__table ::v-deep .caret-wrapper {
  display: none !important;
}

.custom-sort-icon {
  margin-left: 6px;
  color: #c0c4cc;
  font-weight: bold;
  display: inline-block;
  font-size: 14px;
}

.custom-sort-icon.active {
  color: #3a7afe;
}

.detail-drawer__table-panel {
  flex: 1;
  min-height: 0;
  padding: 0 28px 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: 12px;
}

.detail-drawer__table ::v-deep .el-table__body-wrapper {
  overflow: auto !important;
}

.detail-drawer__table ::v-deep .el-table {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e8eef7;
}

.detail-drawer__table ::v-deep .el-table::before,
.detail-drawer__table ::v-deep .el-table__fixed::before,
.detail-drawer__table ::v-deep .el-table__fixed-right::before {
  display: none;
}

.detail-drawer__table ::v-deep th.el-table__cell {
  padding: 13px 0;
  background: #f7f9fc;
  color: #606266;
  font-weight: 700;
  border-bottom: 1px solid #ebeef5;
}

.detail-drawer__table ::v-deep td.el-table__cell {
  padding: 12px 0;
  color: #303133;
  border-bottom: 1px solid #edf2f7;
}

.detail-drawer__table ::v-deep .el-table--striped .el-table__body tr.el-table__row--striped td.el-table__cell {
  background: #fafbfd;
}

.detail-drawer__table ::v-deep .el-table__body tr:hover > td.el-table__cell {
  background: #f5f9ff;
}

.detail-drawer__table ::v-deep .el-table__body-wrapper::-webkit-scrollbar,
.detail-drawer__table ::v-deep .el-table__header-wrapper::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.detail-drawer__table ::v-deep .el-table__body-wrapper::-webkit-scrollbar-track,
.detail-drawer__table ::v-deep .el-table__header-wrapper::-webkit-scrollbar-track {
  background: #eef3fb;
  border-radius: 999px;
}

.detail-drawer__table ::v-deep .el-table__body-wrapper::-webkit-scrollbar-thumb,
.detail-drawer__table ::v-deep .el-table__header-wrapper::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #92bcff, #4b8dff);
  border-radius: 999px;
  border: 2px solid #eef3fb;
}

.detail-drawer__table ::v-deep .el-table__fixed-right {
  box-shadow: -16px 0 20px rgba(72, 120, 196, 0.12);
}

.detail-drawer__pagination {
  position: sticky;
  bottom: 0;
  z-index: 2;
  padding: 14px 0 0;
  margin-top: auto;
  background: linear-gradient(180deg, rgba(248, 251, 255, 0) 0%, rgba(248, 251, 255, 0.96) 18%, rgba(248, 251, 255, 1) 100%);
}


.export-dialog__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.export-dialog__hint {
  font-size: 13px;
  color: #6b7a90;
  line-height: 1.6;
}

.export-dialog__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.export-dialog__group {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
}

.export-dialog__checkbox {
  margin-right: 0;
}

@media (max-width: 1280px) {
  .overview-sidebar {
    flex-basis: 300px;
    max-width: 300px;
  }

  .filter-item {
    width: 190px;
  }

  .date-range {
    width: 300px;
  }
}

@media (max-width: 1100px) {
  .overview-shell {
    flex-direction: column;
  }

  .overview-sidebar {
    position: static;
    width: 100%;
    max-width: none;
  }

  .dashboard-card-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .overview-page {
    padding: 16px;
  }

  .overview-shell {
    gap: 16px;
  }

  .overview-hero {
    padding: 18px 16px 16px;
  }

  .dashboard-card-grid,
  .detail-drawer__summary,
  .detail-drawer__summary--compact {
    grid-template-columns: 1fr;
  }

  .filter-card,
  .table-card {
    padding: 18px 16px;
  }

  .filter-row__actions {
    margin-left: 0;
  }

  .filter-item,
  .date-range {
    width: 100%;
  }

  .detail-drawer__table {
    width: 100%;
  }

  .detail-drawer__header,
  .detail-drawer__summary,
  .detail-drawer__control-card,
  .detail-drawer__table-panel {
    padding-left: 12px;
    padding-right: 12px;
  }

  .detail-drawer__control-card {
    margin-left: 12px;
    margin-right: 12px;
  }

  .export-dialog__toolbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .export-dialog__group {
    grid-template-columns: 1fr;
  }
}
</style>
